import type { AxiosError } from 'axios';
import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import shallow from 'zustand/shallow';

import APIService from '../../../services/api.service';
import { getSucceededRefreshItems } from '../components/duplicate-parts/utils/duplicate-parts.utils';
import { useConnectedItemsState } from '../state/connected-items.state';
import { useDuplicatePartsModalState } from '../state/duplicate-parts-modal.state';
import { useTableDataState } from '../state/table-data.state';
import type { Item } from '../types/item.types';
import type { RefreshResponse } from '../types/items.api';

const PROCESSING_COOLDOWN_MS = 5500;

type OperationType = 'refresh' | 'reset';

export const useRefreshMutation = (operationType: OperationType) => {
   const [isProcessing, setIsProcessing] = useState(false);
   const queryClient = useQueryClient();
   const query = useTableDataState((state) => state.query);
   const { updateRefreshResponses, updateRefreshRequests, raiseRequestErrors, clearResult } = useConnectedItemsState(
      (state) => ({
         updateRefreshResponses: state.updateRefreshResponses,
         updateRefreshRequests: state.updateRefreshRequests,
         raiseRequestErrors: state.raiseRequestErrors,
         clearResult: state.clearResult,
      }),
      shallow
   );

   const { mutate } = useMutation((items: Item[]) => APIService.refreshItems(items, operationType === 'reset'), {
      onMutate: (items: Item[]) => {
         setIsProcessing(true);
         clearResult();
         updateRefreshRequests(
            items.map((item) => item.itemNo.toString()),
            operationType
         );
      },
      onSuccess: (response: RefreshResponse) => {
         updateRefreshResponses([response]);

         if (operationType === 'refresh') {
            const succeededItems = getSucceededRefreshItems(response);

            if (succeededItems.length > 0) {
               void useDuplicatePartsModalState
                  .getState()
                  .scanForDuplicates(succeededItems.map(({ itemNo, message }) => ({ itemNo, message })));
            }
         }

         queryClient.invalidateQueries(['data', query]);
         setTimeout(() => setIsProcessing(false), PROCESSING_COOLDOWN_MS);
      },
      onError: (error: AxiosError) => {
         console.error(`${operationType} mutation error:`, error.message);
         raiseRequestErrors();
         setIsProcessing(false);
      },
   });

   return { mutate, isProcessing };
};
